import {
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type ProfileWidgetProps,
  type ProfileThemingProps,
  PROFILE_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionProfile for imperative control.
 */
export interface LegionProfileHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildProfileWidgetUrl(props: ProfileWidgetProps): string {
  const { baseUrl, theme = "light" } = props;

  const url = new URL("/widget/profile", baseUrl);
  url.searchParams.set("theme", theme);

  // Append theming CSS variable params
  const themingKeys = Object.keys(PROFILE_THEMING_PROP_TO_PARAM) as Array<
    keyof ProfileThemingProps
  >;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(PROFILE_THEMING_PROP_TO_PARAM[key], String(value));
    }
  }

  return url.toString();
}

/**
 * LegionProfile – a React component that embeds the Legion Profile widget
 * via an iframe.
 *
 * @example
 * ```tsx
 * import { LegionProfile } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   return (
 *     <LegionProfile
 *       baseUrl="https://your-legion-domain.com"
 *       authToken="your-jwt-token"
 *       theme="light"
 *     />
 *   );
 * }
 * ```
 */
export const LegionProfile = forwardRef<LegionProfileHandle, ProfileWidgetProps>(
  function LegionProfile(props, ref) {
    const {
      theme = "light",
      authToken,
      width = "400px",
      height = "400px",
      className,
      style,
      onLoad,
      onError,
      baseUrl,
      ...rest
    } = props;

    const iframeRef = useRef<HTMLIFrameElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => {
          const iframe = iframeRef.current;
          if (iframe?.contentWindow) {
            iframe.contentWindow.location.reload();
          }
        },
      }),
      []
    );

    const src = useMemo(
      () => buildProfileWidgetUrl(props),
      [
        baseUrl,
        theme,
        ...Object.keys(PROFILE_THEMING_PROP_TO_PARAM).map(
          (k) => rest[k as keyof typeof rest]
        ),
      ]
    );

    const handleLoad = useCallback(() => {
      // Send auth token via postMessage
      const iframe = iframeRef.current;
      if (authToken && iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          { type: "AUTH_TOKEN", token: authToken },
          baseUrl
        );
      }
      onLoad?.({ widget: "profile", theme: theme ?? "light" });
    }, [onLoad, theme, authToken, baseUrl]);

    const handleError = useCallback(() => {
      onError?.("Failed to load profile widget");
    }, [onError]);

    return (
      <div
        className={className}
        style={{ width, height, ...style }}
        data-testid="legion-profile"
      >
        <iframe
          ref={iframeRef}
          src={src}
          title="Profile Widget"
          width="100%"
          height="100%"
          frameBorder="0"
          scrolling="no"
          onLoad={handleLoad}
          onError={handleError}
          style={{ border: "none" }}
        />
      </div>
    );
  }
);

